import * as GlobalConstants from "../constants/global";
import { IActivateBasketFullModel } from "../models/ActivateGiftCard.model";
import { AssetUpload } from "../models/AssetUpload.type";
import { BuyGiftCardModelOld, IBuyGiftCardOld } from "../models/BuyGiftCard.model";
import ICountry from "../models/Country.interface";
import { CheckoutBillingAddressData } from "../models/ElavonCheckoutModels";
import { IGiftCardCheckoutResult } from "../models/GiftCardCheckoutResult.model";
import IGiftCardGif from "../models/GiftCardGif.model";
import IGiftCardTemplate from "../models/GiftCardTemplate.model";
import { ICheckoutSessionData, IPaymentStep } from "../models/PaymentStep.model";
import { IRegiftCard, RegiftCardModel } from "../models/RegiftCard.model";
import IState from "../models/State.interface";
import { ICheckBalanceResponse } from "../models/api/CheckBalanceResponse.model";
import { BuyGiftCardModel as BuyGiftCardModelNew, IBuyGiftCardModel as IBuyGiftCardNew } from "../models/buy-giftcard/BuyGiftCard.model";
import { IAppSettings } from "../models/interfaces/AppSettings.interface";
import jsonCountriesData from "./static-data/countries.json";
import jsonGifsData from "./static-data/gift-card-gifs.json";
import jsonTemplatesData from "./static-data/gift-card-templates.data.json";
import jsonUSAStatesData from "./static-data/usa-states.json";

const API_URL = GlobalConstants.API_URL;
const useStaticJson = false;

//#region TEMPLATES & MEDIAS

const getGiftCardsTemplates = async (culture: string): Promise<Array<IGiftCardTemplate>> => {
    try {
        if (useStaticJson) {
            return jsonTemplatesData;
        } else {
            //GET 
            const response = await fetch(API_URL + `giftCards/${culture}`);
            if (response.ok) return response.json();
        }


    } catch {
        console.error("Cannot get gift card templates!");
        return null;
    }
};

const getGiftCardsGifsTemplates = async (culture: string, searchTerm: string): Promise<Array<IGiftCardGif>> => {
    try {
        if (useStaticJson) {
            return jsonGifsData;
        } else {
            //GET
            const queryString = {
                s: searchTerm
            };

            const response = await fetch(API_URL + `gif/search?${new URLSearchParams(queryString).toString()}`);
            if (response.ok) return response.json();
        }
    } catch {
        console.error("Cannot get Gifs templates!");
        return null;
    }
};

const uploadGiftCardTemplate = async (culture: string, basketId: string, formData: FormData): Promise<AssetUpload> => {
    try {
        const uploadAssetUrl = API_URL + `assets/template/${culture}/${basketId}`;

        const requestOptions = {
            method: 'POST',
            body: formData
        };

        const response = await fetch(uploadAssetUrl, requestOptions);
        if (!response.ok) {
            throw Error(response.status.toString());
        }

        return await response.json();
    } catch {
        console.error("An error occurred while uploading gift card template!");
        return null;
    }
};

const uploadGiftCardMedia = async (culture: string, basketId: string, formData: FormData): Promise<AssetUpload> => {
    try {
        const uploadAssetUrl = API_URL + `assets/media/${culture}/${basketId}`;

        const requestOptions = {
            method: 'POST',
            body: formData
        };

        const response = await fetch(uploadAssetUrl, requestOptions);
        if (!response.ok) {
            throw Error(response.status.toString());
        }

        return await response.json();
    } catch {
        return null;
    }
};

//#endregion

//#region BUY GIFTCARD

const getBuyGiftCardBasket = async (culture: string, basketId: string, isUsingNewDesign: boolean): Promise<IBuyGiftCardOld | IBuyGiftCardNew> => {
    try {
        //GET
        const response = await fetch(API_URL + `buy/basket/${culture}/${basketId}?isUsingNewDesign=${isUsingNewDesign}`);

        if (response.ok) {
            const resObj = await response.json();
            return resObj;
        }
    } catch {
        console.error("Cannot initialize buy giftcard basket!");
        return null;
    }
};

const updateBuyGiftCardBasket = async (culture: string, basketId: string, model: BuyGiftCardModelOld | BuyGiftCardModelNew, isUsingNewDesign: boolean): Promise<IBuyGiftCardOld | IBuyGiftCardNew> => {
    if (!model) {
        return Promise.resolve(null);
    }

    try {
        let apiUrl = API_URL + `buy/post-basket/${culture}`;
        if (!isUsingNewDesign) {
            apiUrl = API_URL + `buy/basket/${culture}/${basketId}`;
        }

        const requestOptions = {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(model),
        };

        const response = await fetch(apiUrl, requestOptions);
        if (!response.ok) {
            throw Error(response.status.toString());
        }

        return await response.json();
    } catch {
        console.error("An error occurred while updating basket!");
        return Promise.resolve(null);
    }
};

const checkoutBuyGiftCard = async (culture: string, basketId: string, model: BuyGiftCardModelOld | BuyGiftCardModelNew, isUsingNewDesign: boolean): Promise<IBuyGiftCardOld> => {
    if (!model) {
        return Promise.resolve(null);
    }

    try {
        let apiUrl = API_URL + `buy/checkout-new/${culture}`;

        if (!isUsingNewDesign) {
            apiUrl = API_URL + `buy/checkout/${culture}/${basketId}`;
        }

        const requestOptions = {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(model),
        };

        const response = await fetch(apiUrl, requestOptions);
        if (!response.ok) {
            throw Error(response.status.toString());
        }

        return await response.json();
    } catch {
        return Promise.resolve(null);
    }
};

const getCheckoutData = async (basketId: string, amount: number, currency: string): Promise<ICheckoutSessionData> => {
    try {
        const apiUrl = API_URL + `create-creditcard-checkout?bid=${basketId}&amount=${amount}&curr=${currency}`;
        const response = await fetch(apiUrl);

        if (response.ok) {
            return await response.json();
        }

        const errorMsg = await response.text();
        console.error(`Unable to get checkout session key. ${errorMsg}`);

        return null;
    } catch (err) {
        console.error("Unhandled exception was thrown while retrieving the checkout data");
        return null;
    }
};

//#endregion

//#region REGIFT CARD

const checkCardBalance = async (culture: string, basketId: string, cardCode: string, recaptchaToken: string): Promise<ICheckBalanceResponse | string> => {
    try {
        //POST
        const requestOptions = {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                cardCode: cardCode,
                recaptchaToken: recaptchaToken
            })
        };

        const response = await fetch(API_URL + `regift/balance/${culture}/${basketId}`, requestOptions);

        if (response.ok) {
            return response.json();
        } else if (response.status === 401) {
            const resJson = await response.json();

            const defaultErrorMessage = "ReCaptcha verification failed.";
            let errMsg = defaultErrorMessage;

            if (resJson && resJson.errorMessage) {
                errMsg = resJson.errorMessage;
            }

            console.error(errMsg);
            return defaultErrorMessage;
        }

        return null as ICheckBalanceResponse;
    } catch {
        console.error("Cannot check card balance");
        return null as ICheckBalanceResponse;
    }
};

const getRegiftCardBasket = async (culture: string, basketId: string): Promise<IRegiftCard> => {
    try {
        //GET 
        const response = await fetch(API_URL + `regift/basket/${culture}/${basketId}`);
        if (response.ok) {
            return response.json();
        }
    } catch {
        console.error("Cannot get regift card basket!");
        return null;
    }
};

const updateRegiftCardBasket = async (culture: string, basketId: string, model: RegiftCardModel): Promise<IRegiftCard> => {
    if (!model) {
        return Promise.resolve(null);
    }

    try {
        const apiUrl = API_URL + `regift/basket/${culture}/${basketId}`;
        const requestOptions = {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(model),
        };

        const response = await fetch(apiUrl, requestOptions);
        if (!response.ok) {
            throw Error(response.status.toString());
        }

        return await response.json();
    } catch {
        console.error("An error occurred while updating basket!");
        return Promise.resolve(null);
    }
};

const checkoutRegiftCard = async (culture: string, basketId: string, model: RegiftCardModel): Promise<IRegiftCard> => {
    if (!model) {
        return Promise.resolve(null);
    }

    try {

        const apiUrl = API_URL + `regift/checkout/${culture}/${basketId}`;

        const requestOptions = {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(model),
        };

        const response = await fetch(apiUrl, requestOptions);
        if (!response.ok) {
            throw Error(response.status.toString());
        }

        return await response.json();
    } catch {
        console.error("An error occurred while checking out regift basket!");
        return Promise.resolve(null);
    }
};

//#endregion

//#region ACTIVATE GIFTCARD

const checkLuhnPattern = async (activationNumber: string): Promise<boolean> => {
    try {
        const apiUrl = API_URL + `check-luhn-pattern?actNum=${activationNumber}`;
        const response = await fetch(apiUrl);
        const resJson = await response.json();

        return resJson.IsValid;
    } catch (err) {
        console.error(`The activation number does not have valid luhn pattern.\r\n${err}`);
        return false;
    }
}

const checkGiftCardCode = async (activationNumber: string): Promise<{ status: string, message: string }> => {
    try {
        const apiUrl = API_URL + `check?actNum=${activationNumber}`;
        const response = await fetch(apiUrl);
        const resJson = await response.json();

        return {
            status: resJson.Status,
            message: resJson.Message
        };
    } catch (err) {
        console.error(`An error occurred while checking giftcard code.\r\n${err}`);

        return {
            status: "error",
            message: err
        };
    }
};

const updateGiftCardBasket = async (activationNumber: string, loadAmount: string): Promise<IActivateBasketFullModel> => {
    try {
        const apiUrl = API_URL + `update-basket`;
        const data = {
            activationCode: activationNumber,
            unitPrice: loadAmount
        };

        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(data)
        });

        const responseJson = await response.json();
        if (response.ok && responseJson) {
            localStorage.setItem(GlobalConstants.LOCALSTORAGE_KEY_ACTIVATE_BASKET_ID, responseJson.basketId);

            return {
                basketId: responseJson.basketId,
                activationNo: responseJson.activationCode,
                amountNoFees: responseJson.amountNoFees,
                fees: responseJson.fees,
                totalAmount: responseJson.totalAmount,
                firstName: responseJson.firstName ?? "",
                lastName: responseJson.lastName ?? "",
                email: responseJson.email ?? "",
                phone: responseJson.phone ?? ""
            };
        }

        console.error(responseJson);
        throw Error(responseJson?.message ?? "Cannot process response from the API in DataService.");
    } catch (err) {
        console.error("An error occurred while updating giftcard basket");
        console.error(err);
    }
};

const saveActivateGiftCardBasket = async (model: IActivateBasketFullModel, clearBasketQueryString: string): Promise<{ status: string, message: string }> => {
    try {
        let apiUrl = API_URL + `activate-basket-cache`;
        let data = {};

        if (model) {
            data = {
                basketId: model.basketId,
                activationCode: model.activationNo,
                amountInfo: {
                    amountNoFees: model.amountNoFees ?? 0,
                    fees: model.fees ?? 0,
                    totalAmount: model.totalAmount
                },
                billingDetails: {
                    firstName: model.firstName,
                    lastName: model.lastName,
                    email: model.email,
                    phone: model.phone ?? ""
                },
            };
        } else if (clearBasketQueryString) {
            apiUrl += clearBasketQueryString;
        }

        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(data)
        });

        const responseJson = await response.json();
        if (response.ok && responseJson) {
            return {
                status: "ok",
                message: ""
            };
        }

        console.error("Could not save basket in cache. API returned invalid response.");
        console.error(responseJson.message);

        return {
            status: "error",
            message: responseJson.message
        };
    } catch (err) {
        console.error("An exception was thrown while saving giftcard basket in cache.");
        console.error(err);

        return {
            status: "error",
            message: err
        };
    }
};

const clearBasketCache = async (basketId: string): Promise<{ status: string, message: string }> => {
    const clearBasketQueryString = `?basketId=${basketId}&clearBasketCache=true`;
    return await saveActivateGiftCardBasket(null, clearBasketQueryString);
};

const getActivateGiftCardBasket = async (basketId: string): Promise<IActivateBasketFullModel> => {
    try {
        const apiUrl = `${API_URL}activate-basket-cache?basketId=${basketId}`;

        const response = await fetch(apiUrl);

        if (response.status === 404) {
            console.warn("Basket does not exist in cache.");
            return null;
        }

        const responseJson = await response.json();

        if (response.ok && responseJson) {
            return {
                basketId: responseJson.basketId,
                activationNo: responseJson.activationCode,
                amountNoFees: responseJson.amountInfo.amountNoFees,
                fees: responseJson.amountInfo.fees,
                totalAmount: responseJson.amountInfo.totalAmount,
                firstName: responseJson.billingDetails.firstName,
                lastName: responseJson.billingDetails.lastName,
                email: responseJson.billingDetails.email,
                phone: responseJson.billingDetails.phone
            };
        }

        return null;
    } catch (err) {
        console.error("An exception was thrown while getting giftcard basket from cache.");
        console.error(err);
        return null;
    }
}

//#endregion

//#region COMMON

const getAppSettings = async (culture: string, settingsNodeId: string): Promise<IAppSettings | any> => {
    try {
        const response = await fetch(API_URL + `settings/${settingsNodeId}/${culture}`);

        if (response.ok) {
            return await response.json();
        }

        const errRes = await response.text();
        console.error(errRes);
    } catch {
        console.error("Cannot get app settings!");
        return null;
    }
};

const getCountries = async (culture: string): Promise<Array<ICountry>> => {
    try {
        return jsonCountriesData;
    } catch {
        console.error("Cannot get countries!");
        return null;
    }
};

const getUSAStates = async (): Promise<Array<IState>> => {
    try {
        return jsonUSAStatesData;
    } catch {
        console.error("Cannot get USA states!");
        return null;
    }
};

const getTotalProcessingKey = async (basketId: string, amount: number, currency: string, workflow: string): Promise<string> => {
    try {
        const apiUrl = API_URL + `create-creditcard-checkout?bid=${basketId}&workflow=${workflow}`;
        const response = await fetch(apiUrl);

        const resText = await response.text();

        if (response.ok) {
            return resText;
        }

        throw Error(resText);
    } catch (err) {
        console.error("An error occurred while retrieving the totalProcessing checkoutId");
        console.error(err);
    }
};

const updateBasketPaymentStepCache = async (culture: string, basketId: string, model: IPaymentStep): Promise<IPaymentStep> => {
    if (!model) {
        return Promise.resolve(null);
    }

    try {
        const apiUrl = API_URL + `buy/basket-cache/${culture}/${basketId}`;

        const requestOptions = {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(model),
        };

        const response = await fetch(apiUrl, requestOptions);
        if (!response.ok) {
            throw Error(response.status.toString());
        }

        return await response.json();
    } catch {
        console.error("An error occurred while updating basket cache!");
        Promise.resolve(null);
    }

    return Promise.resolve(null);
};

const updateBuyGiftCardModelCache = async (model: IBuyGiftCardNew, culture: string): Promise<IBuyGiftCardNew> => {
    if (!model) {
        return Promise.resolve(null);
    }

    try {
        const apiUrl = API_URL + `buy/save-model-cache/${culture}/${model.id}`;

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(model),
        });

        if (!response.ok) {
            throw Error(response.status.toString());
        }

        const resObj = await response.json();

        if (!resObj) {
            throw Error('Empty model returned');
        }

        //console.log(resObj);
        return resObj;
    } catch (err) {
        console.error(`An error occurred while updating BuyGiftCard model cache! ${err}`);
        Promise.resolve(null);
    }
};

const submitCreditCardPayment = async (workflow: string, basketId: string, sessionKey: string, cardId: string, billingAddress: CheckoutBillingAddressData): Promise<IGiftCardCheckoutResult> => {
    const ipResponse = await fetch('https://api.ipify.org?format=json');
    const ipData = await ipResponse.json();
    const notificationUrl = `${window.location.origin}/giftcard/v1/${workflow.toLowerCase()}/3d-secure/${basketId}`;

    const model = {
        basketId: basketId,
        merchantKey: sessionKey,
        cardIdentifier: cardId,
        notificationUrl: notificationUrl,
        billingAddress: billingAddress,
        browserInformation: {
            ipAddress: ipData.ip,
            acceptHeader: navigator.userAgent,
            javascriptEnabled: true,
            javaEnabled: navigator.javaEnabled(),
            language: navigator.language,
            colourDepth: getBrowserColorDepth(screen.colorDepth),
            screenHeight: screen.height,
            screenWidth: screen.width,
            timeZone: new Date().getTimezoneOffset(),
            userAgent: navigator.userAgent,
            challengeWindowSize: calculateChallengeWindowSize()
        }
    };

    try {
        const response = await fetch(`${API_URL}creditcard/checkout?workflow=${workflow}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(model)
        });

        if (response.ok) {
            return await response.json();
        } else if (response.status === 400) {
            const resJson = await response.json();
            console.error('CreditCardCheckout returned BadRequest');
            console.error(resJson);
            return resJson;
        } else {
            console.log('Error: ', response.status, response.statusText);
        }
    } catch (err) {
        console.error("An unhandled error was thrown while sending CreditCardCheckout request.");
        console.error(err);
    }
};

//#endregion

//#region PRIVATE HELPERS
function calculateChallengeWindowSize() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    let challengeWindowSize = '';

    if (width <= 250 && height <= 400) {
        challengeWindowSize = 'FullScreen';
    } else if (width <= 390 && height <= 400) {
        challengeWindowSize = 'Small';
    } else if (width <= 500 && height <= 600) {
        challengeWindowSize = 'Medium';
    } else if (width <= 600 && height <= 400) {
        challengeWindowSize = 'Large';
    } else {
        challengeWindowSize = 'ExtraLarge';
    }

    return challengeWindowSize;
}

function getBrowserColorDepth(colorDepth: number) {
    const allowedColorDepths = [1, 4, 8, 15, 16, 24, 32, 48];

    if (!allowedColorDepths.includes(colorDepth)) {
        for (let i = allowedColorDepths.length - 1; i >= 0; i--) {
            if (allowedColorDepths[i] <= colorDepth) {
                return allowedColorDepths[i];
            }
        }
    }

    return colorDepth;
}

//#endregion

const DataService = {
    getGiftCardsTemplates,
    getGiftCardsGifsTemplates,
    uploadGiftCardTemplate,
    uploadGiftCardMedia,

    getBuyGiftCardBasket,
    updateBuyGiftCardBasket,
    checkoutBuyGiftCard,
    getCheckoutData,

    checkCardBalance,
    getRegiftCardBasket,
    updateRegiftCardBasket,
    checkoutRegiftCard,

    checkLuhnPattern,
    checkGiftCardCode,
    updateGiftCardBasket,
    saveActivateGiftCardBasket,
    clearBasketCache,
    getActivateGiftCardBasket,

    getAppSettings,
    getCountries,
    getUSAStates,
    getTotalProcessingKey,
    updateBasketPaymentStepCache,
    updateBuyGiftCardModelCache,
    submitCreditCardPayment
};

export default DataService;